import { Arrange, Skeleton, useSnackbar } from "@fullscript/aviary-web";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { PageFooter } from "@shared/layouts";
import { useLocation } from "@shared/react-router-dom/react-router-dom";
import {
  LabsDirectCheckoutEligibilityReason,
  LabsDirectCheckoutItemType,
} from "@shared/types/graphqlGenerated";
import { SwiftFillSteps } from "@shared/types/SwiftFillSteps";
import { gRecaptchaExecute } from "@shared/utils/gRecaptchaExecute/gRecaptchaExecute";
import { DirectLabsAuthStartForm } from "@unauthenticated/signupSignin/components/DirectLabsAuthStartForm/DirectLabsAuthStartForm";
import {
  DirectLabsBlockedState,
  type BlockedClass,
} from "@unauthenticated/signupSignin/components/DirectLabsBlockedState/DirectLabsBlockedState";
import { DirectLabsOrderSummary } from "@unauthenticated/signupSignin/components/DirectLabsOrderSummary/DirectLabsOrderSummary";
import { JourneyCheckoutHeaderNav } from "@unauthenticated/signupSignin/components/JourneyCheckoutHeaderNav/JourneyCheckoutHeaderNav";
import { JourneyCheckoutLayout } from "@unauthenticated/signupSignin/components/JourneyCheckoutLayout/JourneyCheckoutLayout";
import { JourneyEmailFormContent } from "@unauthenticated/signupSignin/components/JourneyEmailFormContent/JourneyEmailFormContent";
import { JourneyLoginForm } from "@unauthenticated/signupSignin/components/JourneyLoginForm/JourneyLoginForm";
import { JourneyMobileOrderSummary } from "@unauthenticated/signupSignin/components/JourneyMobileOrderSummary/JourneyMobileOrderSummary";
import { JourneyPatientInfoForm } from "@unauthenticated/signupSignin/components/JourneyPatientInfoForm/JourneyPatientInfoForm";
import { JourneySuccessContent } from "@unauthenticated/signupSignin/components/JourneySuccessContent/JourneySuccessContent";
import { useDirectLabsCheckoutEligibilityUnauthenticatedSignUpSignInQuery } from "@unauthenticated/signupSignin/data/queries/DirectLabsCheckoutEligibilityQuery/DirectLabsCheckoutEligibility.query.generated";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import { usePatientNextLoginStepUnauthenticatedSignUpSignInMutation } from "../../scenes/PatientAuthStartPage/EmailCheckForm/PatientNextLoginStep.mutation.generated";

import * as styles from "./DirectLabsCheckoutPage.styles";

const URL_PARAMS = {
  MAGIC_LINK_PAGE: "magic_link_page",
  MAGIC_LINK_EMAIL: "magic_link_email",
} as const;

const MAGIC_LINK_PAGES = {
  EMAIL: "email",
  SUCCESS: "success",
} as const;

type MagicLinkPage = (typeof MAGIC_LINK_PAGES)[keyof typeof MAGIC_LINK_PAGES];

const isMagicLinkPage = (value: string | null): value is MagicLinkPage =>
  value === MAGIC_LINK_PAGES.EMAIL || value === MAGIC_LINK_PAGES.SUCCESS;

interface LoginState {
  email: string;
  nextStep: SwiftFillSteps;
}

interface PatientInfoState {
  email: string;
}

interface MagicLinkState {
  email?: string;
  page: MagicLinkPage;
}

export interface DirectLabsCheckoutLabItem {
  id: string;
  itemType: "test" | "panel";
  name?: string | null;
  price?: number | null;
  testCount?: number | null;
}

interface Props {
  storeId: string;
  storeSlug: string;
  labItem: DirectLabsCheckoutLabItem;
}

const DirectLabsCheckoutPage = ({ storeId, storeSlug, labItem }: Props) => {
  const { phoneLarge } = useBreakpoints();
  const { criticalSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const location = useLocation();

  const authenticatedRedirectPath = useMemo(
    () => `/r/checkout/${storeSlug}/labs/${labItem.itemType}/${encodeURIComponent(labItem.id)}`,
    [labItem.id, labItem.itemType, storeSlug]
  );

  // Pre-auth the visitor's state is unknown, so spreeStateId is omitted and the
  // state-restriction check is deferred (re-checked post-auth and at order
  // creation). Blocked verdicts here are structural, or state-restricted once
  // state context flows through (BUT-1110).
  const {
    data: eligibilityData,
    loading: eligibilityLoading,
    error: eligibilityError,
  } = useDirectLabsCheckoutEligibilityUnauthenticatedSignUpSignInQuery({
    variables: {
      storeSlug,
      itemType:
        labItem.itemType === "panel"
          ? LabsDirectCheckoutItemType.Panel
          : LabsDirectCheckoutItemType.Test,
      itemId: labItem.id,
    },
  });

  const verdict = eligibilityData?.viewer?.labsDirectCheckoutEligibility;
  const blockedClass: BlockedClass | null = useMemo(() => {
    if (!verdict || verdict.eligible) return null;

    return verdict.reason === LabsDirectCheckoutEligibilityReason.StateRestricted
      ? "stateRestricted"
      : "structural";
  }, [verdict]);

  const getInitialMagicLinkState = (): MagicLinkState | null => {
    const params = new URLSearchParams(location.search);
    const magicLinkPage = params.get(URL_PARAMS.MAGIC_LINK_PAGE);
    const magicLinkEmail = params.get(URL_PARAMS.MAGIC_LINK_EMAIL);

    if (isMagicLinkPage(magicLinkPage)) {
      return {
        page: magicLinkPage,
        email: magicLinkEmail || undefined,
      };
    }

    return null;
  };

  const [currentUserEmail, setCurrentUserEmail] = useState("");
  const currentUserEmailRef = useRef("");
  const [loginState, setLoginState] = useState<LoginState | null>(null);
  const [patientInfoState, setPatientInfoState] = useState<PatientInfoState | null>(null);
  const [magicLinkState, setMagicLinkState] = useState<MagicLinkState | null>(
    getInitialMagicLinkState()
  );

  const updateUrlWithMagicLinkState = (state: MagicLinkState | null) => {
    const params = new URLSearchParams(location.search);

    params.delete(URL_PARAMS.MAGIC_LINK_PAGE);
    params.delete(URL_PARAMS.MAGIC_LINK_EMAIL);

    if (state) {
      params.set(URL_PARAMS.MAGIC_LINK_PAGE, state.page);
      if (state.email) {
        params.set(URL_PARAMS.MAGIC_LINK_EMAIL, state.email);
      }
    }

    const queryString = params.toString();
    const newUrl = `${location.pathname}${queryString ? `?${queryString}` : ""}`;
    window.history.replaceState({}, "", newUrl);
  };

  const updateMagicLinkState = (state: MagicLinkState | null) => {
    setMagicLinkState(state);
    updateUrlWithMagicLinkState(state);
  };

  const [checkPatientNextLoginStep, { loading: userCheckLoading }] =
    usePatientNextLoginStepUnauthenticatedSignUpSignInMutation({
      onCompleted: data => {
        const userCheckResult = data?.auth?.patientNextLoginStep;

        if (userCheckResult?.errors?.message) {
          criticalSnackbar(userCheckResult.errors.message);

          return;
        }

        const nextStep = userCheckResult?.nextStep;
        const submittedEmail = currentUserEmailRef.current;

        switch (nextStep) {
          case SwiftFillSteps.CREATE_ACCOUNT:
            setPatientInfoState({ email: submittedEmail });
            break;
          case SwiftFillSteps.PASSWORD:
            setLoginState({ email: submittedEmail, nextStep });
            break;
          case SwiftFillSteps.MAGIC_LINK:
            updateMagicLinkState({
              email: submittedEmail,
              page: MAGIC_LINK_PAGES.SUCCESS,
            });
            break;
          default:
            criticalSnackbar(t(l.directLabsCheckout.authStart.errors.generalError));
        }
      },
      onError: error => {
        criticalSnackbar(error?.message || t(l.directLabsCheckout.authStart.errors.generalError));
      },
    });

  const handleContinue = (email: string) => {
    setCurrentUserEmail(email);
    currentUserEmailRef.current = email;

    gRecaptchaExecute(
      (captchaToken: string) => {
        checkPatientNextLoginStep({
          variables: {
            input: {
              attributes: {
                email,
                redirectPath: authenticatedRedirectPath,
                storeId,
              },
              captchaToken,
            },
          },
        });
      },
      () => {
        criticalSnackbar(t(l.directLabsCheckout.authStart.errors.recaptchaError));
      }
    );
  };

  const handleAuthSuccess = () => {
    window.location.assign(authenticatedRedirectPath);
  };

  const handleMagicLinkClick = () => {
    updateMagicLinkState({
      email: loginState?.email || currentUserEmailRef.current,
      page: MAGIC_LINK_PAGES.EMAIL,
    });
  };

  const handleMagicLinkEmailChange = (email: string) => {
    updateMagicLinkState({
      email,
      page: magicLinkState?.page || MAGIC_LINK_PAGES.EMAIL,
    });
  };

  const handleBackToStart = () => {
    setLoginState(null);
    setPatientInfoState(null);
    updateMagicLinkState(null);
  };

  const handleBackNavigation = () => {
    if (loginState || patientInfoState || magicLinkState) {
      handleBackToStart();

      return;
    }

    window.history.back();
  };

  const { name: itemName, price: itemPrice } = labItem;
  // A structurally unavailable item must not present purchase details; a
  // state-restricted item is a real catalog item the visitor simply can't buy
  // here, so its summary stays visible alongside the restricted message.
  const hasItemDetails =
    itemName !== null &&
    itemName !== undefined &&
    itemPrice !== null &&
    itemPrice !== undefined &&
    !eligibilityError &&
    blockedClass !== "structural";

  const renderOrderSummary = () => {
    if (!hasItemDetails || itemName === null || itemName === undefined) return null;
    if (itemPrice === null || itemPrice === undefined) return null;

    return (
      <DirectLabsOrderSummary
        itemType={labItem.itemType}
        name={itemName}
        price={itemPrice}
        testCount={labItem.testCount ?? 1}
      />
    );
  };

  const renderStartForm = () => (
    <DirectLabsAuthStartForm
      initialEmail={currentUserEmail}
      loading={userCheckLoading}
      onContinue={handleContinue}
    />
  );

  const renderLoginForm = () => (
    <JourneyLoginForm
      onLoginSuccess={handleAuthSuccess}
      onMagicLinkClick={handleMagicLinkClick}
      email={loginState?.email}
      storeSlug={storeSlug}
      storeId={storeId}
      authenticatedRedirectPath={authenticatedRedirectPath}
      loginState={loginState || undefined}
    />
  );

  const renderPatientInfoForm = () => (
    <JourneyPatientInfoForm
      onPatientCreated={handleAuthSuccess}
      email={patientInfoState?.email}
      storeId={storeId}
      authenticatedRedirectPath={authenticatedRedirectPath}
      source="labs_self_checkout"
      patientInfoState={patientInfoState || undefined}
    />
  );

  const renderMagicLinkEmailForm = () => (
    <JourneyEmailFormContent
      email={magicLinkState?.email || ""}
      storeId={storeId}
      setEmail={handleMagicLinkEmailChange}
      setPage={page => {
        if (page === MAGIC_LINK_PAGES.SUCCESS) {
          updateMagicLinkState({
            email: magicLinkState?.email,
            page: MAGIC_LINK_PAGES.SUCCESS,
          });
        }
      }}
      onBackToLogin={handleBackToStart}
      authenticatedRedirectPath={authenticatedRedirectPath}
    />
  );

  const renderMagicLinkSuccess = () => (
    <JourneySuccessContent
      email={magicLinkState?.email || ""}
      setPage={page => {
        if (page === MAGIC_LINK_PAGES.EMAIL) {
          updateMagicLinkState({
            email: magicLinkState?.email,
            page: MAGIC_LINK_PAGES.EMAIL,
          });
        }
      }}
      isSwiftFillSignIn={false}
      onBackToLogin={handleBackToStart}
    />
  );

  const renderLoadingSkeleton = () => (
    <div role="status" aria-busy="true" aria-label={t(l.directLabsCheckout.loading)}>
      <Arrange isVertical gap="large" isFullWidth>
        <Skeleton width="100%" height={32} />
        <Skeleton width="100%" height={64} />
        <Skeleton width="100%" height={48} />
      </Arrange>
    </div>
  );

  const renderLeftContent = () => {
    // Show a skeleton (not a blank column) while the eligibility verdict
    // resolves, so blocked visitors never flash the sell flow.
    if (eligibilityLoading) {
      return renderLoadingSkeleton();
    }

    if (eligibilityError) {
      return <DirectLabsBlockedState blockedClass="structural" />;
    }

    if (blockedClass) {
      return <DirectLabsBlockedState blockedClass={blockedClass} />;
    }

    if (magicLinkState?.page === MAGIC_LINK_PAGES.EMAIL) {
      return renderMagicLinkEmailForm();
    }

    if (magicLinkState?.page === MAGIC_LINK_PAGES.SUCCESS) {
      return renderMagicLinkSuccess();
    }

    if (patientInfoState) {
      return renderPatientInfoForm();
    }

    if (loginState) {
      return renderLoginForm();
    }

    return renderStartForm();
  };

  const isMobile = !phoneLarge.greaterThan;

  return (
    <div css={styles.pageContainer}>
      <JourneyCheckoutHeaderNav onBack={handleBackNavigation} />
      <div css={styles.contentArea}>
        {isMobile ? (
          <div css={styles.mobileLayout}>
            {!!hasItemDetails && (
              <JourneyMobileOrderSummary orderTotal={itemPrice ?? 0}>
                {renderOrderSummary()}
              </JourneyMobileOrderSummary>
            )}
            {renderLeftContent()}
          </div>
        ) : (
          <JourneyCheckoutLayout
            leftContent={renderLeftContent()}
            rightContent={renderOrderSummary()}
          />
        )}
      </div>
      <PageFooter type="checkout" hideShippingDisclaimer />
    </div>
  );
};

export { DirectLabsCheckoutPage };
